import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { LayoutGrid, ArrowLeft, Package } from "lucide-react";

import { get } from "@/lib/api-client";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import type { Category } from "@/types/catalog";

export function CategoriesPage() {
  // Fetch all active categories
  const { data, isLoading } = useQuery({
    queryKey: ["categories-all"],
    queryFn: () => get<Category[]>("/catalog/categories"),
  });

  return (
    <div className="max-w-6xl mx-auto space-y-6 p-4 sm:p-6 lg:p-8 animate-fade-in">
      
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Link to="/" className="w-10 h-10 flex items-center justify-center bg-white border border-surface-200 rounded-xl hover:bg-surface-50 transition-colors">
          <ArrowLeft className="w-5 h-5 text-surface-600" />
        </Link>
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-surface-900 tracking-tight">All Categories</h1>
          <p className="text-sm text-surface-500 font-medium mt-1">Browse all available product categories</p>
        </div>
      </div>

      {/* Grid */}
      {isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {Array.from({ length: 12 }).map((_, i) => (
            <Skeleton key={i} className="aspect-square rounded-2xl" />
          ))}
        </div>
      ) : data && data.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 sm:gap-6">
          {data.map((cat) => (
            <Link
              key={cat.id}
              to={`/catalog?category=${cat.id}`}
              className="group bg-white border border-surface-200 rounded-2xl p-5 flex flex-col items-center justify-center text-center hover:border-brand-300 hover:shadow-xl transition-all duration-300 hover:-translate-y-1.5"
            >
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-brand-50 to-surface-100 border border-surface-200 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-sm">
                <LayoutGrid className="w-8 h-8 text-brand-600" />
              </div>
              <h3 className="font-extrabold text-surface-900 text-sm sm:text-base line-clamp-2 leading-tight group-hover:text-brand-700 transition-colors">
                {cat.name}
              </h3>
              <span className="text-[10px] font-bold text-surface-400 mt-2 bg-surface-50 px-2 py-1 rounded-md uppercase tracking-wider">
                Explore
              </span>
            </Link>
          ))}
        </div>
      ) : (
        <EmptyState icon={Package} title="No categories found" description="Check back later." />
      )}
    </div>
  );
}